import { Box, Chip, Link } from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import { FactSource, Stance } from "../../types/productPlane";
import EntityLink from "../Detail/EntityLink";
import { CellPart, CompareCell, STANCE_WORDS } from "./compareModel";

// The small parts a cell of the comparison is drawn from: the spans of its
// statement, the mark of a stance, its sources, and what its tooltip says.

// The sources a cell writes out under "Show the sources" before it counts
// the rest
const SOURCES_SHOWN = 3;

// What stands in a cell with nothing on record: never "no"
export const NOTHING = (
  <Box component="span" title="Nothing on record" sx={{ color: "text.disabled" }}>
    —
  </Box>
);

// The mark of a stance: a dot for has, a ring in the same colour for
// enables (the host has the capability, not the product), a hollow amber
// dot for requires, a cross for lacks. A decorative one (in the legend) is
// hidden from assistive technology.
export const StanceMark: React.FC<{ stance: Stance; decorative?: boolean }> = ({
  stance,
  decorative = false,
}) => {
  const naming = decorative
    ? ({ "aria-hidden": true } as const)
    : ({ role: "img", "aria-label": STANCE_WORDS[stance] } as const);
  if (stance === "lacks") {
    return (
      <Box
        component="span"
        {...naming}
        sx={{ color: "text.disabled", opacity: 0.8, fontSize: 12 }}
      >
        ✕
      </Box>
    );
  }
  const dot = {
    display: "inline-block",
    verticalAlign: "middle",
    borderRadius: "50%",
    boxSizing: "content-box",
  } as const;
  return (
    <Box
      component="span"
      {...naming}
      sx={
        stance === "has"
          ? { ...dot, width: 10, height: 10, backgroundColor: "link.main" }
          : {
              ...dot,
              width: 6,
              height: 6,
              border: "2px solid",
              borderColor: stance === "requires" ? "chart.need" : "link.main",
            }
      }
    />
  );
};

const chipSx = { ml: 0.5, height: 20, fontSize: 11.5 } as const;

const Part: React.FC<{ part: CellPart }> = ({ part }) => {
  switch (part.kind) {
    case "mono":
      return (
        <Box component="span" sx={monospaceStyle}>
          {part.text}
        </Box>
      );
    case "dim":
      return (
        <Box component="span" sx={{ color: "text.disabled" }}>
          {part.text}
        </Box>
      );
    case "link":
      return (
        <EntityLink
          id={part.entity.id}
          type={part.entity.type}
          name={part.text}
        />
      );
    case "stance":
      // "has" is the plain case and reads from its mark alone; every other
      // stance is also written out
      return (
        <>
          <StanceMark stance={part.stance} />
          {part.stance !== "has" && (
            <Box component="span" sx={{ color: "text.disabled" }}>
              {` ${part.text}`}
            </Box>
          )}
        </>
      );
    case "chip":
      return (
        <Chip
          label={part.text}
          size="small"
          variant="outlined"
          color={part.tone === "warning" ? "warning" : "default"}
          sx={chipSx}
        />
      );
    case "disagreement":
      return (
        <Box
          component="span"
          title="The sources give different values"
          sx={{ color: "chart.need", fontSize: 11.5, ml: 0.75, whiteSpace: "nowrap" }}
        >
          ⚠ <Box component="span" sx={monospaceStyle}>{part.text}</Box>
        </Box>
      );
    default:
      return <>{part.text}</>;
  }
};

// The statement of a cell: its parts, one after the other
export const CellParts: React.FC<{ parts: CellPart[] }> = ({ parts }) => (
  <>
    {parts.map((part, index) => (
      <Part key={index} part={part} />
    ))}
  </>
);

// "site · date" of a document; its title where no site is known
const sourceLabel = (source: FactSource): string =>
  [source.site ?? source.title ?? "source", source.date]
    .filter(Boolean)
    .join(" · ");

// The sources of a cell, written under it: site and date, linked to the
// document, three at most and then the number of the rest
export const CellSources: React.FC<{ sources: FactSource[] }> = ({
  sources,
}) => {
  if (sources.length === 0) {
    return null;
  }
  const more = sources.length - SOURCES_SHOWN;
  return (
    <Box
      component="ul"
      aria-label="Sources"
      sx={{
        listStyle: "none",
        m: 0,
        mt: 0.5,
        p: 0,
        fontSize: 11.5,
        lineHeight: 1.5,
        color: "text.disabled",
      }}
    >
      {sources.slice(0, SOURCES_SHOWN).map((source) => (
        <Box component="li" key={source.document_id} sx={monospaceStyle}>
          {source.url ? (
            <Link
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
            >
              {sourceLabel(source)}
            </Link>
          ) : (
            sourceLabel(source)
          )}
        </Box>
      ))}
      {more > 0 && (
        <Box component="li" sx={monospaceStyle}>
          +{more}
        </Box>
      )}
    </Box>
  );
};

// What a cell says on hover: the first passage quoted, or else the first
// statement, and the number of documents behind the cell
export const CellTip: React.FC<{ cell: CompareCell }> = ({ cell }) => {
  const quote = cell.sources.find((source) => source.quote)?.quote;
  const statement = cell.sources.find((source) => source.statement)?.statement;
  const count = cell.sources.length;
  return (
    <Box sx={{ fontSize: 12.5, lineHeight: 1.45, maxWidth: 330 }}>
      {quote ? <Box>“{quote}”</Box> : statement && <Box>{statement}</Box>}
      <Box sx={{ color: "text.secondary", mt: quote || statement ? 0.5 : 0 }}>
        <Box component="span" sx={monospaceStyle}>
          {count}
        </Box>{" "}
        {count === 1 ? "source" : "sources"}
      </Box>
    </Box>
  );
};
